import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-slate-200 relative overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 * index }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02, y: -5 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Background decorative gradients */}
      <motion.div 
        className="absolute -top-10 -right-10 w-20 h-20 rounded-full bg-primary/10"
        animate={{ 
          scale: isHovered ? 1.2 : 1,
          opacity: isHovered ? 0.2 : 0.1
        }}
        transition={{ duration: 0.5 }}
      />
      <motion.div 
        className="absolute -bottom-10 -left-10 w-20 h-20 rounded-full bg-secondary/10"
        animate={{ 
          scale: isHovered ? 1.2 : 1,
          opacity: isHovered ? 0.2 : 0.1
        }}
        transition={{ duration: 0.5 }}
      />
      
      <div className="relative">
        <motion.div 
          className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center text-white mb-4 relative z-10"
          whileHover={{ rotate: 5 }}
          animate={{ 
            boxShadow: isHovered 
              ? '0 10px 15px -3px rgba(227, 6, 19, 0.3)' 
              : '0 4px 6px -1px rgba(227, 6, 19, 0.1)'
          }}
          transition={{ duration: 0.3 }}
        >
          {icon}
          
          {/* Animated particles around icon when hovered */}
          {isHovered && (
            <>
              {[...Array(5)].map((_, i) => (
                <motion.div 
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-primary/30"
                  initial={{ 
                    x: 0, 
                    y: 0, 
                    opacity: 0.7 
                  }}
                  animate={{ 
                    x: (i % 2 === 0 ? 20 : -20) * Math.random(), 
                    y: -20 * Math.random() - 10, 
                    opacity: 0 
                  }}
                  transition={{ 
                    duration: 0.8, 
                    repeat: Infinity, 
                    repeatType: 'loop',
                    delay: i * 0.1
                  }}
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                />
              ))}
            </>
          )}
        </motion.div>
        
        <motion.h3 
          className="text-xl font-bold mb-3"
          animate={{ 
            color: isHovered ? '#e30613' : '#0f172a'
          }}
          transition={{ duration: 0.3 }}
        >
          {title}
        </motion.h3>
        
        <p className="text-slate-600">{description}</p>
        
        <motion.div 
          className="mt-4 flex items-center text-primary font-medium cursor-pointer"
          initial={{ x: -10, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3 + (0.1 * index) }}
          viewport={{ once: true }}
          whileHover={{ x: 5 }}
        >
          <RouterLink to="/services">
          
          <span>Learn more</span>
          </RouterLink>
          <motion.svg 
            className="w-4 h-4 ml-1" 
            viewBox="0 0 20 20" 
            fill="currentColor"
            animate={{ x: isHovered ? 5 : 0 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </motion.svg>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Features: React.FC = () => {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      title: "Personalized Study Plans",
      description: "Customized academic roadmaps tailored to your educational goals, interests, and Japanese language proficiency level."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      title: "Language Support",
      description: "Comprehensive Japanese language courses from beginner to advanced, with specialized academic vocabulary training."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: "Language University Placement",
      description: "Connections with top Japanese School/Universities, helping you find the perfect institution for your academic journey."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Cultural Integration",
      description: "Pre-departure cultural workshops and orientation programs to help you adapt to Japanese society seamlessly."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Visa Assistance",
      description: "Comprehensive support for student visa applications with high success rates and expedited processing."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Scholarship Guidance",
      description: "Expert advice on available scholarships, grants, and financial aid options for international students in Japan."
    }
  ];

  return (
    <section className="py-20 bg-slate-50 relative overflow-hidden">
      {/* Japanese pattern background */}
      <div className="absolute inset-0 opacity-5 pattern-japanese z-0"></div>
      
      {/* Floating decorative elements */}
      <motion.div 
        className="absolute top-20 left-[5%] w-24 h-24 rounded-full bg-primary/10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        animate={{ 
          y: [-15, 0],
          scale: [1.05, 1],
        }}
        transition={{
          duration: 0.8,
          delay: 0.2,
          y: {
            duration: 5,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          },
          scale: {
            duration: 5,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }
        }}
        viewport={{ once: true }}
      />
      
      <motion.div 
        className="absolute bottom-40 right-[10%] w-32 h-32 rounded-full bg-secondary/10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        animate={{ 
          y: [20, 0],
          scale: [1.1, 1],
        }}
        transition={{
          duration: 0.8,
          delay: 0.4,
          y: {
            duration: 7,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          },
          scale: {
            duration: 7,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }
        }}
        viewport={{ once: true }}
      />
      
      <div className="container-custom relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 20,
              delay: 0.1 
            }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <motion.div 
              className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white mb-4 mx-auto"
              whileHover={{ rotate: 5, scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </motion.div>
          </motion.div>
          
          <motion.span 
            className="text-primary font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Our Expertise
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mt-2 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            Comprehensive <span className="text-gradient">Study Abroad</span> Services
          </motion.h2>
          <motion.p 
            className="text-slate-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
            We provide end-to-end support for your academic journey in Japan, from application to graduation and beyond.
          </motion.p>
        </motion.div>
        
        {/* Responsive grid layout */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="w-full h-full">
                <FeatureCard 
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  index={index}
                />
              </div>
            ))}
          </div>
        </div>
        
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          viewport={{ once: true }}
        >
          <RouterLink to="/services"  >
          
          <motion.button 
            className="btn-primary relative overflow-hidden group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10">Explore All Services</span>
            <motion.span 
              className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary"
              initial={{ x: "-100%" }}
              whileHover={{ x: "100%" }}
              transition={{ duration: 1 }}
            />
          </motion.button>
          </RouterLink>
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 